import Decimal from "decimal.js-light";

import {
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
  PRICING_STRATEGY_CUSTOM_FORMULA_LABEL,
  PRICING_STRATEGY_LINEAL_FEET_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_FEET_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
} from "@/utils/constants";

import { INCHES_PER_FOOT } from "./constant";
import { formatInchesAsFraction, parseFractionalInches } from "./measurement.helper";
import {
  sumTotalAreaOfSquareFeetInSquareFeet,
  sumTotalAreaOfSquareFeetInSquareMeters,
  sumTotalAreaOfSquareMetersInSquareMeters,
  sumTotalCustomFormulaQuantity,
  sumTotalLinealFeetInMm,
  sumTotalLinealMetersLengthInMetres,
} from "./measurement-aggregate";
import {
  convertMetersToFeet,
  convertMetersToFeetInches,
  convertMmToFeet,
  convertMmToFeetInches,
  convertSquareMetersToSquareFeet,
} from "./measurement-conversion";
import {
  convertMetresToSquareMetreDimension,
  normalizeSquareMetreEntryUnit,
} from "./square-metre-unit";
/**
 * Normalizes feet and inches by converting inches >= 12 to feet.
 * Ensures inches are always less than 12 by converting excess inches to feet.
 * Handles fractional inches (e.g., "5 1/2", "5.5", "1/2").
 * Returns both decimal values (for calculations) and formatted strings (for display).
 *
 * @param {Object} [params={}] - The measurement object
 * @param {number|string} [params.feet=0] - The number of feet
 * @param {number|string} [params.inches=0] - The number of inches (can be fractional like "5 1/2" or "5.5")
 * @returns {{feet: number, inches: number, feetDisplay: string, inchesDisplay: string}} An object with:
 *   - feet: normalized feet as number (for calculations)
 *   - inches: normalized inches as decimal number (for calculations)
 *   - feetDisplay: normalized feet as number (same as feet, for display)
 *   - inchesDisplay: normalized inches as fraction string (for display, e.g., "5 1/2")
 */
export const normalizeFeetInches = ({ feet = 0, inches = 0 } = {}) => {
  // Parse feet as a number
  const numFeet = Number(parseFloat(feet)) || 0;

  // Parse inches, handling fractional formats
  const numInches = parseFractionalInches(inches);

  // Normalize inches: convert inches >= 12 to feet
  // Use Math.floor to get whole feet from excess inches
  const additionalFeet = Math.floor(numInches / INCHES_PER_FOOT);
  const normalizedInches = numInches % INCHES_PER_FOOT;
  const normalizedFeet = numFeet + additionalFeet;

  // Round to 4 decimal places to avoid floating-point noise (e.g. 2× (1ft 7in) = 38in showing as 2 1/16")
  const roundedInches = Math.round(normalizedInches * 10000) / 10000;

  // Format inches as fraction string for display
  let inchesDisplay = formatInchesAsFraction(roundedInches);
  let finalFeet = normalizedFeet;
  let finalInches = roundedInches;

  // If display rounded to 12 (e.g. fraction edge case), add to feet and show 0 inches
  if (inchesDisplay === "12") {
    finalFeet = normalizedFeet + 1;
    finalInches = 0;
    inchesDisplay = "0";
  }

  return {
    feet: parseFloat(finalFeet),
    inches: parseFloat(finalInches),
    feetDisplay: parseFloat(finalFeet),
    inchesDisplay,
  };
};
/**
 * Formats the total lineal metres for display from Lineal Metres Product.
 * @param {Array} items - The dimensions to format
 * @returns {number} The total lineal metres (4 decimal places)
 */
export const formatTotalLinealMetresForDisplay = ({ items }) => {
  const { total } = sumTotalLinealMetersLengthInMetres({ items, minLengthInMm: 0 });
  return parseFloat(total);
};

/**
 * Formats the total area in square meters for display from Square Metres Product.
 * @param {Array} items - The dimensions to format
 * @returns {number} The total area in square meters (4 decimal places)
 */
export const formatTotalAreaInSquareMetresForDisplay = ({ items }) => {
  const { total } = sumTotalAreaOfSquareMetersInSquareMeters({ items });
  return parseFloat(total);
};
/**
 * Shows one side of a square-metre row in the unit it was typed in.
 *
 * Square-metre rows: `unit` is "m" or "mm"; a missing, null, or unrecognised
 * `unit` (including "MM" in capitals) is a legacy row and means metres.
 * Square-feet rows ignore `unit`; their display data lives in the
 * `lengthFt` / `widthIn` keys and must not come through here.
 *
 * The stored number is always canonical metres, so a millimetre row is shown
 * multiplied by 1000 with any trailing zeros dropped: 2.500123 m reads back as
 * "2500.123mm" and 1.2 m as "1200mm".
 *
 * @param {Object} [params]
 * @param {number|string} params.value - The stored dimension, in metres
 * @param {string} [params.unit] - How the row was entered
 * @returns {string} e.g. "2500.123mm" or "2.5m"
 */
export const formatSquareMetreDimensionForDisplay = ({ value, unit } = {}) => {
  const entryUnit = normalizeSquareMetreEntryUnit(unit);

  return `${convertMetresToSquareMetreDimension({ metres: value, unit: entryUnit })}${entryUnit}`;
};

/**
 * Shows a whole square-metre row as one line, e.g. "3 at 2500mm x 1200mm".
 * Totals stay in square metres - never pass an area through this.
 *
 * @param {Object} [params]
 * @param {Object} [params.row] - A square-metre row; null or undefined reads as an empty row
 * @param {number} [params.row.amount=1] - How many pieces of this size
 * @param {number|string} params.row.length - Stored length, in metres
 * @param {number|string} params.row.width - Stored width, in metres
 * @param {string} [params.row.displayUnit] - How the row was entered
 * @param {string} [params.separator="x"] - Character between the two dimensions
 * @returns {string} The row as one line of text
 */
export const formatSquareMetreForDisplay = ({ row, separator = "x" } = {}) => {
  // `row ?? {}` also covers null, which a parameter default would not.
  // camelCase because every API payload is run through camelcaseKeys before it
  // reaches state; the wire spelling is `display_unit` and snakecaseKeys puts it
  // back on save.
  const { amount, length, width, displayUnit } = row ?? {};

  return `${amount ?? 1} at ${formatSquareMetreDimensionForDisplay({ value: length, unit: displayUnit })} ${separator} ${formatSquareMetreDimensionForDisplay({ value: width, unit: displayUnit })}`;
};

/**
 * Formats the total area in square feet for display from Square Feet Product.
 * @param {Array} items - The dimensions to format
 * @returns {number} The total area in square feet (4 decimal places)
 */
export const formatTotalAreaInSquareFeetForDisplay = ({ items }) => {
  const { total } = sumTotalAreaOfSquareFeetInSquareFeet({ items });
  return parseFloat(total);
};

/**
 * Formats the total area in square meters for display from Square Feet Product.
 * @param {Array} items - The dimensions to format
 * @returns {number} The total area in square meters (4 decimal places)
 */
export const formatTotalAreaOfSquareFeetInSquareMetersForDisplay = ({ items }) => {
  const { total } = sumTotalAreaOfSquareFeetInSquareMeters({ items });
  return parseFloat(total);
};

/**
 * Formats the total lineal feet for display.
 * Minimum length is set to zero since this is used for display only.
 * @param {Array} items - The lengths to format
 * @returns {number} The total lineal feet (4 decimal places)
 */
export const formatTotalLinealFeetForDisplay = ({ items }) => {
  const { total } = sumTotalLinealFeetInMm({ items, minLengthInMm: 0 });

  const { feetDisplay, inchesDisplay } = normalizeFeetInches(convertMmToFeetInches(total));

  return {
    feetDisplay,
    inchesDisplay,
  };
};

export const formatCustomFormulaQuantityForDisplay = ({ quantity, formulaOutput }) => {
  const total = sumTotalCustomFormulaQuantity({ quantity, formulaOutput });
  return parseFloat(total);
};

/**
 * Formats a product quantity for display based on its pricing strategy.
 * Use this when lengths, output are present.
 * @param {Object} params
 * @param {number|string} params.quantity - The base quantity value.
 * @param {string} params.pricingStrategy - The pricing strategy label (e.g. basic, lineal metres, square metres, lineal feet, square feet, custom formula).
 * @param {Array} params.lengthItems - Length/dimension items used for dimension-based strategies.
 * @param {number|string} params.formulaOutput - The formula output value for custom formula strategy.
 * @returns {number|string} Formatted quantity with unit suffix (e.g. "3.5m", "12sqm", "6ft 3in", "10sqft"), a parsed number, or the raw quantity as fallback.
 */
export const formatQuantityForDisplayByPricingStrategy = ({
  quantity,
  pricingStrategy,
  lengthItems,
  formulaOutput,
}) => {
  if (pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
    return parseFloat(quantity);
  }

  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    const total = formatTotalLinealMetresForDisplay({ items: lengthItems });
    return `${total} m`;
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    return `${formatTotalAreaInSquareMetresForDisplay({ items: lengthItems })} sqm`;
  }

  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    const { feetDisplay, inchesDisplay } = formatTotalLinealFeetForDisplay({ items: lengthItems });
    return `${feetDisplay}ft ${inchesDisplay}in`;
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    return `${formatTotalAreaInSquareFeetForDisplay({ items: lengthItems })} sqft`;
  }

  if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
    return formatCustomFormulaQuantityForDisplay({ quantity, formulaOutput }).toString();
  }

  return quantity;
};

/**
 * Formats a pre-calculated quantity for display by appending the appropriate unit suffix.
 * Use this when the quantity is already computed and only needs unit conversion/formatting.
 * Pre-calculated quantity usually means it comes from the backend response most of the time.
 *
 * @param {Object} params
 * @param {number|string} params.quantity - The pre-calculated quantity value (in metres for linea metres and lineal feet while sqm in square metres and square feet).
 * @param {string} params.pricingStrategy - The pricing strategy label.
 * @param {boolean} [params.hideUnitLabel=false] - Whether to hide the unit label in the display string (e.g. show "3.5" instead of "3.5 m").
 * @returns {number|string} Formatted quantity with unit suffix (e.g. "3.5 m", "12 sqm", "6 ft 3 in", "10 sqft"), or the raw quantity as fallback.
 */
export const formatPreCalculatedQuantityForDisplay = ({
  quantity,
  pricingStrategy,
  hideUnitLabel = false,
}) => {
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    if (!quantity) {
      return `0${hideUnitLabel ? "" : " m"}`;
    }
    return `${quantity}${hideUnitLabel ? "" : " m"}`;
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    if (!quantity) {
      return `0${hideUnitLabel ? "" : " sqm"}`;
    }
    return `${quantity}${hideUnitLabel ? "" : " sqm"}`;
  }

  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    if (!quantity) {
      return `0${hideUnitLabel ? "" : " ft"} 0${hideUnitLabel ? "" : " in"}`;
    }
    const { feet, inches } = convertMetersToFeetInches(quantity);
    const { feetDisplay, inchesDisplay } = normalizeFeetInches({ feet, inches });
    return `${feetDisplay}${hideUnitLabel ? "" : " ft"} ${inchesDisplay}${hideUnitLabel ? "" : " in"}`;
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    if (!quantity) {
      return `0${hideUnitLabel ? "" : " sqft"}`;
    }
    return `${convertSquareMetersToSquareFeet(quantity)}${hideUnitLabel ? "" : " sqft"}`;
  }

  return quantity;
};

/**
 * Formats the quantity of the product to be used in the inventory comparison vs quantity.
 * Only use this function for comparing inventory in the sales order page.
 * Do not use this function for any other purpose.
 *
 * @param {Object} params
 * @param {number} params.quantity - The quantity to format
 * @param {string} params.pricingStrategy - The pricing strategy to format
 * @param {Array} params.lengthItems - The lengths to format
 * @returns {number} The formatted quantity
 */
export const convertProductQuantityForInventoryComparison = ({
  quantity,
  pricingStrategy,
  lengthItems,
}) => {
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    return formatTotalLinealMetresForDisplay({ items: lengthItems });
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    return formatTotalAreaInSquareMetresForDisplay({ items: lengthItems });
  }

  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    const { total } = sumTotalLinealFeetInMm({ items: lengthItems });
    const totalInFeet = convertMmToFeet(parseFloat(total));
    return parseFloat(totalInFeet);
  }
  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    return formatTotalAreaInSquareFeetForDisplay({ items: lengthItems });
  }

  if (pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
    return parseFloat(quantity);
  }

  if (pricingStrategy === PRICING_STRATEGY_CUSTOM_FORMULA_LABEL) {
    return parseFloat(quantity);
  }

  return parseFloat(quantity);
};

/**
 * Formats the fields of the inventory response depending on the pricing strategy
 * to be used in the inventory tooltip.
 * Only use this function for displaying inventory and also to compare
 * the formatted inventory to the product quantity in the sales order page.
 * Do not use this function for any other purpose.
 *
 * @param {Object} params
 * @param {Object} params.inventory - Inventory quantities from API
 * @param {number} params.inventory.onHand - On-hand quantity
 * @param {number} params.inventory.promised - Promised quantity
 * @param {number} params.inventory.available - Available quantity
 * @param {number} params.inventory.onTheWay - On-the-way quantity
 * @param {number} [params.inventory.anyQuantity] - Generic quantity from BE when the context (on-hand, promised, etc.) is not specified. Formatted with the same unit suffix as other fields when present.
 * @param {string} params.pricingStrategy - The pricing strategy to format
 * @returns {Object} Inventory values normalized for comparison and display, including anyQuantityDisplay when anyQuantity is provided
 */
export const convertProductInventoryWithFormatting = ({ inventory, pricingStrategy }) => {
  if (!inventory) {
    return {
      onHand: 0,
      promised: 0,
      available: 0,
      onTheWay: 0,
      anyQuantity: 0,
      onHandDisplay: `0`,
      promisedDisplay: `0`,
      availableDisplay: `0`,
      onTheWayDisplay: `0`,
      anyQuantityDisplay: `0`,
    };
  }
  if (pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
    return {
      ...inventory,
      onHandDisplay: `${parseFloat(Decimal(inventory.onHand || 0).toDecimalPlaces(4))} m`,
      promisedDisplay: `${parseFloat(Decimal(inventory.promised || 0).toDecimalPlaces(4))} m`,
      availableDisplay: `${parseFloat(Decimal(inventory.available || 0).toDecimalPlaces(4))} m`,
      onTheWayDisplay: `${parseFloat(Decimal(inventory.onTheWay || 0).toDecimalPlaces(4))} m`,
      ...(inventory?.anyQuantity != null && {
        anyQuantityDisplay: `${parseFloat(Decimal(inventory.anyQuantity || 0).toDecimalPlaces(4))} m`,
      }),
    };
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
    return {
      ...inventory,
      onHandDisplay: `${parseFloat(Decimal(inventory.onHand || 0).toDecimalPlaces(4))} sqm`,
      promisedDisplay: `${parseFloat(Decimal(inventory.promised || 0).toDecimalPlaces(4))} sqm`,
      availableDisplay: `${parseFloat(Decimal(inventory.available || 0).toDecimalPlaces(4))} sqm`,
      onTheWayDisplay: `${parseFloat(Decimal(inventory.onTheWay || 0).toDecimalPlaces(4))} sqm`,
      ...(inventory?.anyQuantity != null && {
        anyQuantityDisplay: `${parseFloat(Decimal(inventory.anyQuantity || 0).toDecimalPlaces(4))} sqm`,
      }),
    };
  }

  if (pricingStrategy === PRICING_STRATEGY_LINEAL_FEET_LABEL) {
    const onHand = convertMetersToFeet(inventory.onHand || 0);
    const promised = convertMetersToFeet(inventory.promised || 0);
    const available = convertMetersToFeet(inventory.available || 0);
    const onTheWay = convertMetersToFeet(inventory.onTheWay || 0);

    const onHandDisplay = normalizeFeetInches(convertMetersToFeetInches(inventory.onHand || 0));
    const promisedDisplay = normalizeFeetInches(convertMetersToFeetInches(inventory.promised || 0));
    const availableDisplay = normalizeFeetInches(
      convertMetersToFeetInches(inventory.available || 0)
    );
    const onTheWayDisplay = normalizeFeetInches(convertMetersToFeetInches(inventory.onTheWay || 0));
    const anyQuantityDisplay =
      inventory?.anyQuantity != null
        ? normalizeFeetInches(convertMetersToFeetInches(inventory.anyQuantity))
        : null;
    return {
      onHand,
      promised,
      available,
      onTheWay,
      onHandDisplay: `${onHandDisplay.feetDisplay}ft ${onHandDisplay.inchesDisplay}in`,
      promisedDisplay: `${promisedDisplay.feetDisplay}ft ${promisedDisplay.inchesDisplay}in`,
      availableDisplay: `${availableDisplay.feetDisplay}ft ${availableDisplay.inchesDisplay}in`,
      onTheWayDisplay: `${onTheWayDisplay.feetDisplay}ft ${onTheWayDisplay.inchesDisplay}in`,
      ...(inventory?.anyQuantity != null && {
        anyQuantityDisplay: `${anyQuantityDisplay.feetDisplay}ft ${anyQuantityDisplay.inchesDisplay}in`,
      }),
    };
  }

  if (pricingStrategy === PRICING_STRATEGY_SQUARE_FEET_LABEL) {
    const onHand = convertSquareMetersToSquareFeet(inventory.onHand || 0);
    const promised = convertSquareMetersToSquareFeet(inventory.promised || 0);
    const available = convertSquareMetersToSquareFeet(inventory.available || 0);
    const onTheWay = convertSquareMetersToSquareFeet(inventory.onTheWay || 0);
    const anyQuantityDisplay =
      inventory?.anyQuantity != null
        ? convertSquareMetersToSquareFeet(inventory.anyQuantity)
        : null;
    return {
      onHand,
      promised,
      available,
      onTheWay,
      onHandDisplay: `${onHand} sqft`,
      promisedDisplay: `${promised} sqft`,
      availableDisplay: `${available} sqft`,
      onTheWayDisplay: `${onTheWay} sqft`,
      ...(inventory?.anyQuantity != null && { anyQuantityDisplay: `${anyQuantityDisplay} sqft` }),
    };
  }

  return {
    ...inventory,
    onHandDisplay: inventory.onHand,
    promisedDisplay: inventory.promised,
    availableDisplay: inventory.available,
    onTheWayDisplay: inventory.onTheWay,
    ...(inventory?.anyQuantity != null && { anyQuantityDisplay: inventory.anyQuantity }),
  };
};
